import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
} from "react";
import "./AgentChat.css";
import "./LoadingDots.css";
import { useSelector } from "react-redux";
import {
  selectUser,
  selectMergeConnected,
  setLinkRecords,
  selectLastViewedMessageId,
} from "../../redux/features/appData";
import toast, { Toaster } from "react-hot-toast";
import useWebSocket from "react-use-websocket";
import { useParams } from "react-router-dom";
import {
  apiAISaveAgentResponse,
  apiGetConversationHistoryPaginated,
  apiGetMessagesAfter,
  apiGetMessagesBefore,
  apiStartNewThread,
  apiAreMessagesOutOfSync,
} from "../../services/AIService";
import { HumanMessage as HumanMessageComponent } from "./HumanMessage/HumanMessage";
import { AIMessage } from "./AIMessage";
import { AICanvasMessage } from "./AICanvasMessage";
import { ToolMessage } from "./ToolMessage";
import { NewThreadMessage } from "./NewThreadMessage";
import { StatusMessage } from "./StatusMessage";
import { StatusMessageType } from "winslow-core";
import { marked } from "marked";

import {
  AgentResponseMessage,
  AIResponseCanvasMessage,
  HumanMessage,
  MessageType,
  MessageThreadType,
  ClassificationRoute,
  Workspace,
  Employee,
  PersonaExpanded,
} from "winslow-core";
import {
  addMessage,
  selectCurrentThread,
  selectEmployees,
  selectMessages,
  selectPersonas,
  selectPrompt,
  setEmployees,
  setMessages,
  setPersonas,
  setPrompt,
  prependMessages,
  appendMessages,
  loadMessagesAroundTarget,
  setLoadingMore,
  setLoadingMoreUp,
  setLoadingMoreDown,
  setPaginationData,
  setTargetMessageId,
  selectHasMoreMessages,
  selectHasMoreMessagesUp,
  selectHasMoreMessagesDown,
  selectIsLoadingMore,
  selectIsLoadingMoreUp,
  selectIsLoadingMoreDown,
  selectIsInitialLoading,
  selectTargetMessageId,
  setInitialLoading,
  setEmployeeRecords,
  setCompanyDocuments,
  setWorkspaceUsers,
  updateCanvasContent,
  openCanvasMode,
  setCanvasMessage,
  updateSpecificMessage,
  selectDocumentPreviewModalIsOpen,
  selectDocumentPreviewModalDocument,
  closeDocumentPreviewModal,
} from "../../redux/features/agent/agentSlice";
import { useDispatch } from "react-redux";
import { apiGetEmployees } from "../../services/EmployeeService";
import { apiGetPersonas } from "../../services/PersonasService";
import CirclePersonaEmployeeSelect from "../common/CirclePersonaEmployeeSelect";
import config from "environment";
import React from "react";
import { MentionsInput, Mention } from "react-mentions";
import { AIStreamResponse } from "../../hooks/useAIStream";
import { AIMessageStreaming } from "./AIMessageStreaming";
import { ModalDebugChat } from "./ModalDebugChat";
import ModalBasicConfirm from "../common/ModalBasicConfirm";
import { LoadingPage } from "../common/LoadingPage";
import { useMentions } from "../../hooks/useMentions";
import { DCComponentRouter } from "../app/dynamic-components/DCComponentRouter";
import { apiGetCompanyDocumentsForWorkspace } from "../../services/CompanyDocumentsService";
import { apiGetWorkspaceLinkRecords, apiGetWorkspaceUsers } from "../../api";
import { apiGetEmployeeRecordsForWorkspace } from "../../services/EmployeeRecordService";
import { CanvasMode } from "../canvas-mode/CanvasMode";
import { useCanvasMode } from "../canvas-mode/hooks/useCanvasMode";
import { EmployeeProfileSidebar } from "../employee-profile-sidebar/EmployeeProfileSidebar";
import { toastFail, toastSuccess } from "../../config";
import { CanvasMessage } from "../canvas-mode/types";
import { DCReporting } from "../app/dynamic-components/DCReporting";
import { HumanMessageCanvasFollowUp } from "./HumanMessageCanvasFollowUp";
import { HumanMessageReportingRequest } from "./HumanMessageReportingRequest";
import { ArrowUp, Loader } from "lucide-react";
import { Button } from "../../ds2/components";
import { DCEmployeeRecordProfileCard } from "../app/dynamic-components/DCEmployeeRecordProfileCard";
import { DocumentPreviewModal } from "../wazawan/documents/DocumentPreviewModal";

const notProd = config.env !== "production";

const MentionsInputAny = MentionsInput as unknown as React.FC<any>;
const MentionAny = Mention as unknown as React.FC<any>;

type AgentChatProps = {};

export const AgentChat = (props: AgentChatProps) => {
  const storeUser = useSelector(selectUser);
  const dispatch = useDispatch();
  const messages = useSelector(selectMessages);
  const currentThread = useSelector(selectCurrentThread);
  const prompt = useSelector(selectPrompt);
  const employees = useSelector(selectEmployees);
  const personas = useSelector(selectPersonas);
  const hasMoreMessages = useSelector(selectHasMoreMessages);
  const hasMoreMessagesUp = useSelector(selectHasMoreMessagesUp);
  const hasMoreMessagesDown = useSelector(selectHasMoreMessagesDown);
  const isLoadingMore = useSelector(selectIsLoadingMore);
  const isLoadingMoreUp = useSelector(selectIsLoadingMoreUp);
  const isLoadingMoreDown = useSelector(selectIsLoadingMoreDown);
  const isInitialLoading = useSelector(selectIsInitialLoading);
  const targetMessageId = useSelector(selectTargetMessageId);
  let lastViewedMessageId = useSelector(selectLastViewedMessageId);

  // Document Preview Modal integration
  const isDocumentPreviewModalOpen = useSelector(
    selectDocumentPreviewModalIsOpen
  );
  const previewDocument = useSelector(selectDocumentPreviewModalDocument);

  // Canvas Mode integration
  const {
    isOpen: isCanvasModeOpen,
    openCanvas,
    closeCanvas,
    message: canvasMessage,
  } = useCanvasMode();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const isLocalChangeRef = useRef<boolean>(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [plainTextPrompt, setPlainTextPrompt] = useState<string>("");

  const { getMentionData, renderMentionSuggestion } = useMentions();

  const [currentResponse, setCurrentResponse] =
    useState<AIStreamResponse | null>(null);

  const [currentStatusMessage, setCurrentStatusMessage] =
    useState<StatusMessageType | null>(null);
  const [currentMessageData, setCurrentMessageData] = useState<any>({});
  const [currentUIObject, setCurrentUIObject] = useState<string | null>(null);
  const [currentSavedMessage, setCurrentSavedMessage] =
    useState<AgentResponseMessage | null>(null);
  const [currentAsset, setCurrentAsset] = useState<string | null>(null);

  const statusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const canvasUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [lastPrompt, setLastPrompt] = useState<string>("");
  const streamingScrollCounterRef = useRef<number>(0);
  const canvasStreamChunkCountRef = useRef<number>(0);
  const canvasStreamStartTimeRef = useRef<number | null>(null);
  const canvasAccumulatedContentRef = useRef<string>("");
  const lastCanvasDispatchTimeRef = useRef<number>(0);

  const { collectionId, messageId } = useParams();

  const mergeConnected = useSelector(selectMergeConnected);

  const currentWorkspace = (storeUser?.workspaces?.find(
    (workspace: Workspace) => workspace.id === storeUser?.currentWorkspaceId
  ) || null) as Workspace | null;
  console.log("[currentWorkspace]", currentWorkspace);
  const hasNativeHRIS = Boolean(currentWorkspace?.hasNativeHRIS);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesStartRef = useRef<HTMLDivElement>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [newestUIMessageId, setNewestUIMessageId] = useState<number>(0);

  // NR remove these 10/14
  // const [showEmployeeRecordCard, setShowEmployeeRecordCard] = useState<boolean>(false);
  // const [employeeRecordIdMention, setEmployeeRecordIdMention] = useState<string>("");

  const historyRef = useRef<HTMLDivElement>(null);
  const userHasScrolledUpRef = useRef<boolean>(false);
  const isAutoScrollingRef = useRef<boolean>(false);

  const { sendJsonMessage, lastJsonMessage } =
    useWebSocket<AgentResponseMessage>(config.wsAiUrl, {
      onOpen: () => console.log("[websocket] opened"),
      onClose: (code: any) => console.log("[websocket] closed: ", code),
      shouldReconnect: (closeEvent) => true,
      onError: (event: WebSocketEventMap["error"]) => {
        console.log("[websocket] error: ", event);
        setIsProcessing(false); // Reset processing state on WebSocket error
        setIsLoading(false);
      },
    });

  const [showDebugModal, setShowDebugModal] = useState(false);

  // Error boundary for canvas operations
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      console.error(
        "🎨 [GLOBAL-ERROR] Uncaught error during canvas operation:",
        {
          message: event.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          error: event.error,
          stack: event.error?.stack,
        }
      );
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error("🎨 [GLOBAL-ERROR] Unhandled promise rejection:", {
        reason: event.reason,
        promise: event.promise,
      });
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  // Initialize/sync local markup value with external prompt when not a local change
  useEffect(() => {
    if (isLocalChangeRef.current) {
      isLocalChangeRef.current = false;
      return;
    }
    setInputValue(prompt || "");
    // For now, if prompt is restored, assume it's plain text (legacy compatibility)
    setPlainTextPrompt(prompt || "");
  }, [prompt]);

  useEffect(() => {
    if (parseInt(messageId ?? "0") > 0) {
      lastViewedMessageId = parseInt(messageId ?? "0");
    }
  }, [messageId]);

  // Autosize the underlying textarea on content changes
  useLayoutEffect(() => {
    const el = textareaRef.current as HTMLTextAreaElement | null;
    if (!el) return;

    // Reset height to calculate scroll height
    el.style.height = "auto";

    // Calculate line height (approximately 24px per line)
    const lineHeight = 24;
    const maxLines = 5;
    const maxHeight = lineHeight * maxLines; // 120px for 5 lines

    // Set height based on content, but limit to max height
    if (el.scrollHeight <= maxHeight) {
      // Content fits within 5 lines, expand naturally
      el.style.height = `${el.scrollHeight}px`;
      el.style.overflowY = "hidden";
    } else {
      // Content exceeds 5 lines, set to max height and enable scrolling
      el.style.height = `${maxHeight}px`;
      el.style.overflowY = "auto";
    }
  }, [inputValue]);

  // Modal state for out-of-sync confirmation
  const [showOutOfSyncModal, setShowOutOfSyncModal] = useState(false);

  const openOutOfSyncModal = () => setShowOutOfSyncModal(true);
  const closeOutOfSyncModal = () => setShowOutOfSyncModal(false);

  const refreshMessages = async () => {
    if (!storeUser || !collectionId) {
      console.error("Cannot refresh messages: Missing required data");
      return;
    }

    try {
      console.log("[AgentChat] Refreshing messages...");
      dispatch(setInitialLoading(true));
      setIsProcessing(false); // Reset processing state on refresh

      // Load fresh messages from the server (same as initial load)
      const conversationResult = await apiGetConversationHistoryPaginated(
        storeUser.id,
        collectionId,
        5, // Same limit as initial load
        0 // Start from beginning
      );

      console.log("Messages refresh result:", conversationResult);

      if (conversationResult) {
        dispatch(setMessages(conversationResult.messages));
        let uiMessageIdMax = 0;
        for (const message of conversationResult.messages) {
          if (
            message.type === MessageType.AI_MESSAGE_UI &&
            message.id &&
            message.id > uiMessageIdMax
          ) {
            uiMessageIdMax = message.id;
          }
        }
        setNewestUIMessageId(uiMessageIdMax);
        dispatch(
          setPaginationData({
            hasMore: conversationResult.hasMore,
            totalCount: conversationResult.totalCount,
          })
        );
        console.log("[AgentChat] Messages refreshed successfully");
      }
    } catch (error) {
      console.error("Error refreshing messages:", error);
      dispatch(setInitialLoading(false));
      toast("Failed to refresh messages", toastFail);
    }
  };

  const handleRefresh = async () => {
    try {
      // Close the modal first
      closeOutOfSyncModal();

      // Refresh the messages from the server
      await refreshMessages();

      // Restore saved prompt if it exists
      const saved = sessionStorage.getItem("agentChatSavedPrompt");
      if (saved) {
        dispatch(setPrompt(saved));
        sessionStorage.removeItem("agentChatSavedPrompt");
      }

      toast("Messages refreshed successfully", toastSuccess);
    } catch (error) {
      console.error("Error refreshing messages:", error);
      toast("Failed to refresh messages", toastFail);
    }
  };

  // Immediate scroll function for streaming (no debounce)
  const scrollToBottomImmediate = useCallback(() => {
    if (messagesEndRef.current) {
      isAutoScrollingRef.current = true;
      messagesEndRef.current.scrollIntoView({ behavior: "auto" });
      userHasScrolledUpRef.current = false; // Reset since we're at bottom
      // Reset the auto-scrolling flag after a brief delay
      setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 100);
    }
  }, []);

  // Function to scroll to a specific message by ID
  const scrollToMessage = useCallback((messageId: number) => {
    const messageElement = document.querySelector(
      `[data-message-id="${messageId}"]`
    );
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center" });

      // Add temporary highlight effect
      messageElement.classList.add("message-highlighted");
      setTimeout(() => {
        messageElement.classList.remove("message-highlighted");
      }, 2000);
    }
  }, []);

  // Debounced scroll function for non-streaming scenarios
  const debouncedScrollToBottom = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          if (messagesEndRef.current) {
            isAutoScrollingRef.current = true;
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
            userHasScrolledUpRef.current = false; // Reset since we're at bottom

            // Focus on textarea after scrolling
            setTimeout(() => {
              textareaRef.current?.focus();
              isAutoScrollingRef.current = false;
            }, 100);
          }
        }, 50); // Reduced from 100ms to 50ms debounce
      };
    })(),
    []
  );

  useEffect(() => {
    if (storeUser && collectionId) {
      // Split loading into critical (blocks UI) and non-critical (background)
      const fetchCriticalData = async () => {
        try {
          dispatch(setInitialLoading(true));

          // Load ONLY critical data in parallel - messages and persona selector data
          const criticalPromises = [];

          // Messages are MOST important - show chat first
          let conversationPromise;
          if (messageId && !isNaN(Number(messageId))) {
            // Load messages around the target messageId
            if (notProd) {
              console.log(
                "[AgentChat] Loading messages around messageId:",
                messageId
              );
            }
            dispatch(setTargetMessageId(Number(messageId)));
            conversationPromise = apiGetConversationHistoryPaginated(
              storeUser.id,
              collectionId,
              20, // Larger limit for around-message loading
              0,
              Number(messageId)
            );
          } else {
            // Load only the most recent messages initially (default behavior)
            conversationPromise = apiGetConversationHistoryPaginated(
              storeUser.id,
              collectionId,
              10,
              0
            );
          }
          criticalPromises.push(conversationPromise);

          // These are needed for persona selector to work
          if (storeUser.currentWorkspaceId) {
            criticalPromises.push(
              apiGetEmployees(storeUser.currentWorkspaceId),
              apiGetPersonas(storeUser.currentWorkspaceId)
            );
          }

          const results = await Promise.all(criticalPromises);
          const conversationResult = results[0] as
            | {
                messages: any[];
                totalCount: number;
                hasMore: boolean;
                hasMoreUp?: boolean;
                hasMoreDown?: boolean;
              }
            | undefined;
          const employeesResult = results[1] as Employee[] | undefined;
          const personasResult = results[2] as PersonaExpanded[] | undefined;

          // Process messages
          if (conversationResult) {
            if (messageId && !isNaN(Number(messageId))) {
              dispatch(
                loadMessagesAroundTarget({
                  messages: conversationResult.messages,
                  hasMoreUp: conversationResult.hasMoreUp || false,
                  hasMoreDown: conversationResult.hasMoreDown || false,
                  totalCount: conversationResult.totalCount,
                  targetMessageId: Number(messageId),
                })
              );
            } else {
              dispatch(setMessages(conversationResult.messages));
              dispatch(
                setPaginationData({
                  hasMore: conversationResult.hasMore,
                  totalCount: conversationResult.totalCount,
                  hasMoreUp: conversationResult.hasMoreUp,
                  hasMoreDown: conversationResult.hasMoreDown,
                })
              );
            }

            // Find max UI message ID
            let uiMessageIdMax = 0;
            for (const message of conversationResult.messages) {
              if (
                message.type === MessageType.AI_MESSAGE_UI &&
                message.id &&
                message.id > uiMessageIdMax
              ) {
                uiMessageIdMax = message.id;
              }
            }
            setNewestUIMessageId(uiMessageIdMax);

            if (notProd) {
              console.log(
                "[AgentChat] Initial conversation load result:",
                conversationResult
              );
              console.log(
                "Set initial pagination data - hasMore:",
                conversationResult.hasMore,
                "totalCount:",
                conversationResult.totalCount
              );
            }
          }

          // Process employees and personas
          if (employeesResult) {
            dispatch(setEmployees(employeesResult));
            if (notProd) {
              console.log("[AgentChat] employeesResult", employeesResult);
            }
          }

          if (personasResult) {
            dispatch(setPersonas(personasResult));
            if (notProd) {
              console.log("[AgentChat] personasResult", personasResult);
            }
          }

          // UI is now interactive!
          dispatch(setInitialLoading(false));
        } catch (error) {
          console.error("Error fetching critical data:", error);
          dispatch(setInitialLoading(false));
        }
      };

      const fetchNonCriticalData = async () => {
        // Load supporting data in background - doesn't block UI
        if (!storeUser.currentWorkspaceId) return;

        try {
          const [
            companyDocsResult,
            workspaceUsersResult,
            employeeRecordsResult,
            linkRecordsResult,
          ] = await Promise.all([
            apiGetCompanyDocumentsForWorkspace(storeUser.currentWorkspaceId),
            apiGetWorkspaceUsers(storeUser.currentWorkspaceId),
            apiGetEmployeeRecordsForWorkspace(storeUser.currentWorkspaceId),
            apiGetWorkspaceLinkRecords(storeUser.currentWorkspaceId),
          ]);

          // Dispatch when ready (user is already interacting with chat)
          if (companyDocsResult) {
            dispatch(setCompanyDocuments(companyDocsResult));
          }

          if (workspaceUsersResult) {
            dispatch(setWorkspaceUsers(workspaceUsersResult));
          }

          if (employeeRecordsResult) {
            dispatch(setEmployeeRecords(employeeRecordsResult));
          }

          if (linkRecordsResult) {
            dispatch(setLinkRecords(linkRecordsResult));
          }

          if (notProd) {
            console.log("[AgentChat] Non-critical data loaded in background");
          }
        } catch (error) {
          console.error("Error fetching non-critical data:", error);
          // Non-critical errors don't affect UI
        }
      };

      // Run both, but don't wait for non-critical
      fetchCriticalData();
      fetchNonCriticalData(); // Fire and forget
    }

    // Cleanup function to clear messages when component unmounts
    return () => {
      dispatch(setMessages([]));
      // setNewestUIMessageId(0);
      dispatch(setInitialLoading(true)); // Reset to initial loading state
      setIsProcessing(false); // Reset processing state
      userHasScrolledUpRef.current = false; // Reset scroll tracking state
      isAutoScrollingRef.current = false;
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
      if (canvasUpdateTimeoutRef.current) {
        clearTimeout(canvasUpdateTimeoutRef.current);
      }
    };
  }, [storeUser, collectionId, dispatch, messageId]);

  // Handle scrolling to target message when loaded around a specific messageId (initial load only)
  useEffect(() => {
    if (targetMessageId && messages.length > 0 && !isInitialLoading) {
      // Small delay to ensure DOM is updated with new messages
      setTimeout(() => {
        scrollToMessage(targetMessageId);
        // Clear the target message ID after scrolling to it once
        // This prevents subsequent message updates from scrolling back to this message
        dispatch(setTargetMessageId(null));
      }, 500);
    }
  }, [
    targetMessageId,
    messages.length,
    isInitialLoading,
    scrollToMessage,
    dispatch,
  ]);

  useEffect(() => {
    if (lastJsonMessage) {
      const msg = lastJsonMessage as unknown as AIStreamResponse;

      // Log all incoming messages with stage information
      console.log(`📨 [WS-MESSAGE] Received: ${msg.stage}`, {
        timestamp: new Date().toISOString(),
        stage: msg.stage,
        hasContent: !!msg.content,
        contentLength: msg.content?.length || 0,
        hasSavedMessage: !!(msg as any).savedMessage,
      });

      // AI Streaming Stage Flow:
      // 1. "status" - Status messages from server
      // 2. "starting" - Initial response started (transformed from "base-starting")
      // 3. "streaming" - Content chunks (transformed from "base-streaming")
      // 4. "complete" - Final response with saved message (transformed from "base-complete")
      //
      // Canvas Mode uses separate stages:
      // - "canvas-starting", "canvas-streaming", "canvas-complete"

      if (msg.stage === "status") {
        // Handle status messages
        const statusMessage: StatusMessageType = {
          id: Date.now(),
          type: "STATUS",
          stage: "status",
          message: msg.message || "",
          content: msg.content || "",
          success: msg.success || true,
          personaIds:
            (msg as any).personaIds || (msg as any).data?.personaIds || [],
          data: (msg as any).data,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Clear any existing timeout
        if (statusTimeoutRef.current) {
          clearTimeout(statusTimeoutRef.current);
        }

        console.log("statusMessage", statusMessage);
        // Retain personaIds once they've been received - only update if new ones are provided
        if (
          !statusMessage.personaIds ||
          statusMessage.personaIds.length === 0
        ) {
          if (
            currentStatusMessage &&
            currentStatusMessage.personaIds &&
            currentStatusMessage.personaIds.length > 0
          ) {
            statusMessage.personaIds = currentStatusMessage.personaIds;
          }
        }
        setCurrentStatusMessage(statusMessage);
      } else if (msg.stage === "render") {
        console.log("[AgentChat] Render: ", msg);
        setCurrentResponse(msg);
        setCurrentUIObject(msg.data?.object || null);
        setCurrentMessageData(msg.data || {});
        setCurrentStatusMessage(null); // Clear status message when starting new response
        setIsLoading(false);
        setIsProcessing(false); // Allow sending next message

        // get the last message id
        const message = msg.savedMessage as AgentResponseMessage;
        setNewestUIMessageId(message.id);
        setCurrentSavedMessage(msg.savedMessage as AgentResponseMessage);

        streamingScrollCounterRef.current = 0; // Reset scroll counter
      } else if (msg.stage === "display-asset") {
        console.log("[AgentChat] Display Asset: ", msg);
        setCurrentResponse(msg);
        setCurrentStatusMessage(null); // Clear status message when starting new response
        setIsLoading(false);
        setIsProcessing(false); // Allow sending next imessage
        setCurrentAsset(msg.data);
        setCurrentMessageData(msg.data || {});

        // get the last message id
        const message = msg.savedMessage as AgentResponseMessage;
        setNewestUIMessageId(message.id);
        setCurrentSavedMessage(msg.savedMessage as AgentResponseMessage);

        streamingScrollCounterRef.current = 0; // Reset scroll counter
      } else if (msg.stage === "starting") {
        setCurrentResponse(msg);
        setCurrentStatusMessage(null); // Clear status message when starting new response
        setIsLoading(false);
        streamingScrollCounterRef.current = 0; // Reset scroll counter
      } else if (msg.stage === "streaming") {
        setCurrentResponse((prev) => {
          if (!prev) return null;
          const newContent = prev.content + msg.content;

          // If Canvas Mode is open, update the canvas content with debouncing
          // to prevent excessive DOM manipulation and Redux dispatches
          if (isCanvasModeOpen) {
            // Clear previous timeout to debounce updates
            if (canvasUpdateTimeoutRef.current) {
              clearTimeout(canvasUpdateTimeoutRef.current);
            }

            // Debounce updates to every 100ms to reduce DOM thrashing
            canvasUpdateTimeoutRef.current = setTimeout(() => {
              const htmlContent = marked(newContent) as string;
              dispatch(updateCanvasContent(htmlContent));
            }, 100);
          }

          return {
            ...prev,
            content: newContent,
          };
        });
        // Scroll frequently for smooth streaming experience - every 2nd update
        // Only auto-scroll if user hasn't manually scrolled up
        streamingScrollCounterRef.current += 1;
        if (
          streamingScrollCounterRef.current % 2 === 0 &&
          !userHasScrolledUpRef.current
        ) {
          // Use immediate scroll during streaming for responsiveness
          scrollToBottomImmediate();
        }
      } else if (msg.stage === "canvas-starting") {
        // Handle Canvas Mode document generation start
        canvasStreamChunkCountRef.current = 0;
        canvasStreamStartTimeRef.current = performance.now();
        canvasAccumulatedContentRef.current = ""; // Reset accumulated content
        lastCanvasDispatchTimeRef.current = 0; // Reset last dispatch time

        console.log("🎨 [CANVAS-START] Received canvas-starting", {
          timestamp: new Date().toISOString(),
          isCanvasModeOpen,
          hasCanvasMessage: !!canvasMessage,
          canvasMessageId: canvasMessage?.id,
          currentTitle: canvasMessage?.message.title,
          startTime: canvasStreamStartTimeRef.current,
        });

        dispatch(
          setCanvasMessage({
            ...canvasMessage,
            message: {
              content: "",
              title: canvasMessage?.message.title || "Untitled Document",
            },
          } as CanvasMessage)
        );

        console.log(
          "🎨 [CANVAS-START] Canvas message initialized with empty content"
        );

        setCurrentResponse(msg);
        setCurrentStatusMessage(null);
        setIsLoading(false);
      } else if (msg.stage === "canvas-streaming") {
        // Handle Canvas Mode document generation streaming
        canvasStreamChunkCountRef.current += 1;
        const chunkNumber = canvasStreamChunkCountRef.current;
        const chunkLength = msg.content?.length || 0;
        const newContentLength =
          (canvasMessage?.message.content?.length || 0) + chunkLength;
        const elapsedTime = canvasStreamStartTimeRef.current
          ? performance.now() - canvasStreamStartTimeRef.current
          : 0;

        console.log(`🎨 [CANVAS-STREAM] Chunk #${chunkNumber}`, {
          timestamp: new Date().toISOString(),
          chunkNumber,
          chunkLength,
          chunkPreview: msg.content?.substring(0, 50),
          currentContentLength: canvasMessage?.message.content?.length || 0,
          newContentLength,
          hasCanvasMessage: !!canvasMessage,
          canvasMessageId: canvasMessage?.id,
          elapsedMs: Math.round(elapsedTime),
          chunksPerSecond:
            elapsedTime > 0
              ? (chunkNumber / (elapsedTime / 1000)).toFixed(2)
              : 0,
        });

        if (canvasMessage) {
          try {
            // CRITICAL FIX: Accumulate content in ref to avoid closure issues
            canvasAccumulatedContentRef.current += msg.content;
            const currentAccumulated = canvasAccumulatedContentRef.current;
            const now = performance.now();
            const timeSinceLastDispatch =
              now - lastCanvasDispatchTimeRef.current;

            // Helper function to dispatch the accumulated content
            const dispatchAccumulatedContent = () => {
              const beforeDispatch = performance.now();
              const contentToDispatch = canvasAccumulatedContentRef.current;

              dispatch(
                setCanvasMessage({
                  ...canvasMessage,
                  message: {
                    content: contentToDispatch,
                    title: canvasMessage?.message.title || "Untitled Document",
                  },
                } as CanvasMessage)
              );

              lastCanvasDispatchTimeRef.current = performance.now();
              const dispatchTime = performance.now() - beforeDispatch;

              console.log(`🎨 [CANVAS-STREAM] ✅ Dispatched update`, {
                contentLength: contentToDispatch.length,
                dispatchTimeMs: dispatchTime.toFixed(2),
                chunkNumber,
              });

              if (dispatchTime > 50) {
                console.warn(
                  `🎨 [CANVAS-STREAM] ⚠️ Slow dispatch detected: ${dispatchTime.toFixed(2)}ms`
                );
              }
            };

            // THROTTLE: If it's been more than 1000ms since last dispatch, dispatch immediately
            if (timeSinceLastDispatch >= 1000) {
              // Clear any pending timeout since we're dispatching now
              if (canvasUpdateTimeoutRef.current) {
                clearTimeout(canvasUpdateTimeoutRef.current);
                canvasUpdateTimeoutRef.current = null;
              }

              dispatchAccumulatedContent();

              console.log(
                `🎨 [CANVAS-STREAM] Chunk #${chunkNumber} - Throttled dispatch (${timeSinceLastDispatch.toFixed(0)}ms since last)`,
                {
                  chunkLength,
                  totalAccumulated: currentAccumulated.length,
                }
              );
            } else {
              // DEBOUNCE: Wait a bit before dispatching in case more chunks arrive
              if (canvasUpdateTimeoutRef.current) {
                clearTimeout(canvasUpdateTimeoutRef.current);
              }

              const timeUntilNextDispatch = 1000 - timeSinceLastDispatch;
              canvasUpdateTimeoutRef.current = setTimeout(() => {
                dispatchAccumulatedContent();
              }, timeUntilNextDispatch);

              console.log(
                `🎨 [CANVAS-STREAM] Chunk #${chunkNumber} - Queued for dispatch in ${timeUntilNextDispatch.toFixed(0)}ms`,
                {
                  chunkLength,
                  totalAccumulated: currentAccumulated.length,
                  timeSinceLastDispatch: timeSinceLastDispatch.toFixed(0),
                }
              );
            }
          } catch (error) {
            console.error(
              `🎨 [CANVAS-STREAM] ❌ Error on chunk #${chunkNumber}:`,
              error
            );
            console.error("Error details:", {
              errorMessage:
                error instanceof Error ? error.message : String(error),
              errorStack: error instanceof Error ? error.stack : "No stack",
            });
          }
        } else {
          console.warn(
            `🎨 [CANVAS-STREAM] ⚠️ Chunk #${chunkNumber} - No canvas message available, skipping update`
          );
        }
      } else if (msg.stage === "canvas-complete") {
        // Handle Canvas Mode document generation completion
        const totalChunks = canvasStreamChunkCountRef.current;
        const totalTime = canvasStreamStartTimeRef.current
          ? performance.now() - canvasStreamStartTimeRef.current
          : 0;
        const avgChunksPerSecond =
          totalTime > 0 ? (totalChunks / (totalTime / 1000)).toFixed(2) : 0;

        console.log("🎨 [CANVAS-COMPLETE] ========== START ==========");
        console.log("🎨 [CANVAS-COMPLETE] Stream Summary", {
          totalChunks,
          totalTimeMs: Math.round(totalTime),
          avgChunksPerSecond,
        });
        console.log("🎨 [CANVAS-COMPLETE] Received canvas-complete", {
          timestamp: new Date().toISOString(),
          hasContent: !!msg.content,
          contentLength: msg.content?.length || 0,
          hasSavedMessage: !!msg.savedMessage,
          savedMessageId: msg.savedMessage?.id,
          savedMessageType: msg.savedMessage?.type,
          currentCanvasMessageId: canvasMessage?.id,
          messagesArrayLength: messages.length,
          lastMessageId: messages[messages.length - 1]?.id,
        });

        setIsLoading(false);
        setIsProcessing(false);

        console.log(
          "🎨 [CANVAS-COMPLETE] Set isLoading and isProcessing to false"
        );

        // Clear any pending canvas update timeouts - CRITICAL for cleanup
        if (canvasUpdateTimeoutRef.current) {
          clearTimeout(canvasUpdateTimeoutRef.current);
          canvasUpdateTimeoutRef.current = null;
          console.log(
            "🎨 [CANVAS-COMPLETE] Cleared pending canvas update timeout"
          );
        }

        // Log final accumulated content stats
        console.log("🎨 [CANVAS-COMPLETE] Final content stats", {
          accumulatedLength: canvasAccumulatedContentRef.current.length,
          savedMessageLength: (msg.savedMessage as any)?.message?.content
            ?.length,
          contentMatch:
            canvasAccumulatedContentRef.current.length ===
            (msg.savedMessage as any)?.message?.content?.length,
        });

        // Dispatch final accumulated content before completion (ensures UI shows latest)
        if (canvasAccumulatedContentRef.current && canvasMessage) {
          console.log(
            "🎨 [CANVAS-COMPLETE] Dispatching final accumulated content",
            {
              accumulatedLength: canvasAccumulatedContentRef.current.length,
            }
          );

          dispatch(
            setCanvasMessage({
              ...canvasMessage,
              message: {
                content: canvasAccumulatedContentRef.current,
                title: canvasMessage?.message.title || "Untitled Document",
              },
            } as CanvasMessage)
          );
        }

        // Finalize the canvas content update
        if (msg.content && msg.savedMessage) {
          const savedMsg = msg.savedMessage as any;
          console.log(
            "🎨 [CANVAS-COMPLETE] Processing content and savedMessage",
            {
              savedMessageKeys: Object.keys(msg.savedMessage),
              savedMessageContent: typeof savedMsg.message?.content,
              savedMessageContentLength: savedMsg.message?.content?.length,
            }
          );

          try {
            console.log("🎨 [CANVAS-COMPLETE] Dispatching setCanvasMessage...");
            dispatch(
              setCanvasMessage({
                ...msg.savedMessage,
              } as unknown as CanvasMessage)
            );
            console.log(
              "🎨 [CANVAS-COMPLETE] ✅ setCanvasMessage dispatched successfully"
            );

            console.log(
              "🎨 [CANVAS-COMPLETE] Dispatching updateSpecificMessage..."
            );
            dispatch(updateSpecificMessage(msg.savedMessage));
            console.log(
              "🎨 [CANVAS-COMPLETE] ✅ updateSpecificMessage dispatched successfully"
            );
          } catch (error) {
            console.error(
              "🎨 [CANVAS-COMPLETE] ❌ Error during dispatch:",
              error
            );
            console.error(
              "🎨 [CANVAS-COMPLETE] Error stack:",
              error instanceof Error ? error.stack : "No stack"
            );
          }
        } else {
          console.warn(
            "🎨 [CANVAS-COMPLETE] ⚠️ Missing content or savedMessage",
            {
              hasContent: !!msg.content,
              hasSavedMessage: !!msg.savedMessage,
            }
          );
        }

        // Clear the current response and status for canvas mode
        console.log(
          "🎨 [CANVAS-COMPLETE] Clearing currentResponse and currentStatusMessage"
        );
        setCurrentResponse(null);
        setCurrentStatusMessage(null);

        // Log memory usage if available (Chrome only)
        const perfWithMemory = performance as any;
        if (perfWithMemory.memory) {
          console.log("🎨 [CANVAS-COMPLETE] Memory Usage", {
            usedJSHeapSize: `${(perfWithMemory.memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
            totalJSHeapSize: `${(perfWithMemory.memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
            jsHeapSizeLimit: `${(perfWithMemory.memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`,
            percentUsed: `${((perfWithMemory.memory.usedJSHeapSize / perfWithMemory.memory.jsHeapSizeLimit) * 100).toFixed(2)}%`,
          });
        }

        // Clean up accumulated content ref
        canvasAccumulatedContentRef.current = "";
        console.log("🎨 [CANVAS-COMPLETE] Cleared accumulated content ref");

        console.log("🎨 [CANVAS-COMPLETE] ========== END ==========");

        // Final scroll to bottom
      } else if (msg.stage === "complete") {
        setIsLoading(false);
        setIsProcessing(false); // Allow sending next message

        if (msg.savedMessage) {
          dispatch(addMessage(msg.savedMessage));
        }

        setIsLoading(false);
        setCurrentResponse(null);

        setCurrentStatusMessage(null); // Clear status message when response is complete

        // Only auto-scroll if user hasn't manually scrolled up
        if (!userHasScrolledUpRef.current) {
          // Immediate scroll to bottom to show final formatted result
          scrollToBottomImmediate();

          // Follow up with smooth scroll after content settles
          setTimeout(() => {
            debouncedScrollToBottom();
          }, 100);
        }

        if (msg.savedMessage && msg.savedMessage.id) {
          apiAISaveAgentResponse({
            question: lastPrompt,
            response: msg.savedMessage.content,
            userId: storeUser!.id,
            originatorEmail: undefined,
            originatorName: undefined,
            workspaceId: storeUser!.currentWorkspaceId!,
            collectionId: collectionId!,
            linkRecordIds: msg.sources || [],
            feedback: undefined,
            sources: msg.sources || [],
            messageId: msg.savedMessage.id,
            employeeId: currentThread?.employeeId || null,
          });
        }
      } else if (msg.stage === "update-message") {
        console.log("[AgentChat] Update Message: ", msg);

        if (msg.savedMessage) {
          dispatch(updateSpecificMessage(msg.savedMessage));
        }
      } else {
        console.info("Unknown message stage:", msg);
      }
    }
  }, [lastJsonMessage]);

  // Only scroll to bottom when we should (new messages, not pagination)
  // useEffect(() => {
  //   if (shouldScrollToBottom && !isLoadingMore) {
  //     scrollToBottom();
  //   }
  // }, [messages, shouldScrollToBottom, isLoadingMore]);

  // Scroll when loading state changes (for new messages)
  useEffect(() => {
    if (isLoading) {
      scrollToBottom();
    }

    if (!isLoading && lastViewedMessageId) {
      console.log(
        "[AgentChat] ======= AGENT SCROLLING TO MESSAGE =======\n",
        lastViewedMessageId
      );
    }
  }, [isLoading]);

  // Removed redundant layout effect

  // Function to scroll to the bottom of the chat (for immediate scrolling)
  const scrollToBottom = () => {
    setTimeout(() => {
      if (messagesEndRef.current) {
        isAutoScrollingRef.current = true;
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        userHasScrolledUpRef.current = false; // Reset since we're at bottom

        // Focus on textarea after scrolling
        setTimeout(() => {
          textareaRef.current?.focus();
          isAutoScrollingRef.current = false;
        }, 100);
      }
    }, 150); // Reduced from 300ms to 150ms for faster response
  };

  // Function to load older messages (when scrolling up)
  const loadMoreMessagesUp = async () => {
    if (!storeUser || !collectionId || isLoadingMoreUp || !hasMoreMessagesUp) {
      console.log("loadMoreMessagesUp early return:", {
        hasUser: !!storeUser,
        hasCollectionId: !!collectionId,
        isLoadingMoreUp,
        hasMoreMessagesUp,
      });
      return;
    }

    console.log("Loading older messages - current count:", messages.length);
    dispatch(setLoadingMoreUp(true));

    // Store current scroll position
    const scrollContainer = historyRef.current;
    const previousScrollHeight = scrollContainer?.scrollHeight || 0;

    try {
      let result;

      if (messages.length > 0) {
        // Use the ID of the oldest message for loading before
        const oldestMessage = messages[0];
        result = await apiGetMessagesBefore(
          storeUser.id,
          collectionId,
          oldestMessage.id,
          5
        );
      } else {
        // Fallback to offset-based pagination if no messages
        result = await apiGetConversationHistoryPaginated(
          storeUser.id,
          collectionId,
          5,
          messages.length
        );
      }

      console.log("Load older messages result:", result);

      if (result && result.messages.length > 0) {
        console.log("Adding", result.messages.length, "older messages");
        dispatch(
          prependMessages({
            messages: result.messages,
            hasMore: result.hasMore,
            totalCount: 0, // We don't get totalCount from the new endpoint
          })
        );

        // Update the hasMoreUp flag
        dispatch(
          setPaginationData({
            hasMore: hasMoreMessagesDown,
            totalCount: 0,
            hasMoreUp: result.hasMore,
          })
        );

        let uiMessageIdMax = 0;
        for (const message of result.messages) {
          if (
            message.type === MessageType.AI_MESSAGE_UI &&
            message.id &&
            message.id > uiMessageIdMax
          ) {
            uiMessageIdMax = message.id;
          }
        }

        if (uiMessageIdMax > newestUIMessageId) {
          setNewestUIMessageId(uiMessageIdMax);
        }

        // Restore scroll position after new messages are loaded
        setTimeout(() => {
          if (scrollContainer) {
            const newScrollHeight = scrollContainer.scrollHeight;
            const scrollDifference = newScrollHeight - previousScrollHeight;
            console.log("Scroll restoration - difference:", scrollDifference);
            scrollContainer.scrollTop = scrollDifference;
          }
        }, 100);
      } else {
        // No more messages available - update the hasMoreUp flag
        console.log("No more older messages available");
        dispatch(
          setPaginationData({
            hasMore: hasMoreMessagesDown,
            totalCount: 0,
            hasMoreUp: false,
          })
        );
      }
    } catch (error) {
      console.error("Error loading older messages:", error);
    } finally {
      dispatch(setLoadingMoreUp(false));
    }
  };

  // Function to load newer messages (when scrolling down)
  const loadMoreMessagesDown = async () => {
    if (
      !storeUser ||
      !collectionId ||
      isLoadingMoreDown ||
      !hasMoreMessagesDown
    ) {
      console.log("loadMoreMessagesDown early return:", {
        hasUser: !!storeUser,
        hasCollectionId: !!collectionId,
        isLoadingMoreDown,
        hasMoreMessagesDown,
      });
      return;
    }

    console.log("Loading newer messages - current count:", messages.length);
    dispatch(setLoadingMoreDown(true));

    try {
      if (messages.length > 0) {
        // Use the ID of the newest message for loading after
        const newestMessage = messages[messages.length - 1];
        const result = await apiGetMessagesAfter(
          storeUser.id,
          collectionId,
          newestMessage.id,
          5
        );

        console.log("Load newer messages result:", result);

        if (result && result.messages.length > 0) {
          console.log("Adding", result.messages.length, "newer messages");
          dispatch(
            appendMessages({
              messages: result.messages,
              hasMore: result.hasMore,
            })
          );

          // Update the hasMoreDown flag
          dispatch(
            setPaginationData({
              hasMore: result.hasMore,
              totalCount: 0,
              hasMoreDown: result.hasMore,
            })
          );

          let uiMessageIdMax = 0;
          for (const message of result.messages) {
            if (
              message.type === MessageType.AI_MESSAGE_UI &&
              message.id &&
              message.id > uiMessageIdMax
            ) {
              uiMessageIdMax = message.id;
            }
          }

          if (uiMessageIdMax > newestUIMessageId) {
            setNewestUIMessageId(uiMessageIdMax);
          }
        } else {
          // No more messages available - update the hasMoreDown flag
          console.log("No more newer messages available");
          dispatch(
            setPaginationData({
              hasMore: false,
              totalCount: 0,
              hasMoreDown: false,
            })
          );
        }
      }
    } catch (error) {
      console.error("Error loading newer messages:", error);
    } finally {
      dispatch(setLoadingMoreDown(false));
    }
  };

  // Backward compatibility function - delegates to loadMoreMessagesUp
  const loadMoreMessages = loadMoreMessagesUp;

  // Handle scroll events to detect bidirectional loading needs
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    // Don't track user scrolling if we're auto-scrolling
    if (!isAutoScrollingRef.current) {
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
      userHasScrolledUpRef.current = !isAtBottom;
    }

    // If user scrolled to near the top (within 100px) and we have more messages up
    if (scrollTop < 100 && hasMoreMessagesUp && !isLoadingMoreUp) {
      console.log(
        "Triggering loadMoreMessagesUp - scrollTop:",
        scrollTop,
        "hasMoreMessagesUp:",
        hasMoreMessagesUp,
        "isLoadingMoreUp:",
        isLoadingMoreUp
      );
      loadMoreMessagesUp();
    }

    // If user scrolled to near the bottom (within 100px) and we have more messages down
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
    if (isNearBottom && hasMoreMessagesDown && !isLoadingMoreDown) {
      console.log(
        "Triggering loadMoreMessagesDown - scrollTop:",
        scrollTop,
        "scrollHeight:",
        scrollHeight,
        "clientHeight:",
        clientHeight,
        "hasMoreMessagesDown:",
        hasMoreMessagesDown,
        "isLoadingMoreDown:",
        isLoadingMoreDown
      );
      loadMoreMessagesDown();
    }
  };

  useEffect(() => {
    if (currentThread) {
      console.log("currentThread", currentThread);
      // Scroll when thread changes
      scrollToBottom();
    }
  }, [currentThread]);

  const handleSendMessage = async () => {
    if (!prompt.trim() || !storeUser || !collectionId) {
      if (!storeUser) toast("No user available");
      if (!collectionId) toast("No collection selected");
      return;
    }

    // Set processing state immediately to prevent double submission
    setIsProcessing(true);

    // NR*** remove this its just a hack to test UI components
    // if (prompt.substring(0, 3) === "/e ") {

    //   // parse out ids from string wirh format " employee-record-[uuid can include -'s] "
    //   const employeeRecordIdMention = prompt.match(/employee-record-\w+-\w+-\w+-\w+-\w+/g);
    //   const idOnly = employeeRecordIdMention?.toString().substring("/employee-record-".length-1);

    //   alert("idOnly: '" + idOnly + "'");
    //   setShowEmployeeRecordCard(true);
    //   setEmployeeRecordIdMention(idOnly || "");
    //   console.log("prompt", prompt);
    //   console.log("employeeRecordIdMention", employeeRecordIdMention);
    //   return;
    // }

    // Check for out-of-sync messages before we send
    try {
      const lastMessage = messages[messages.length - 1];
      const lastMessageId = lastMessage ? Number(lastMessage.id) : 0;
      const sessionId = currentThread?.sessionId || "";

      const outOfSync = await apiAreMessagesOutOfSync(lastMessageId, sessionId);

      if (outOfSync) {
        setIsProcessing(false);
        openOutOfSyncModal();
        return; // stop normal flow
      }
    } catch (err) {
      console.error("Error checking message sync", err);
      setIsProcessing(false);
      // allow normal flow if error
    }

    // Store the message content before clearing input
    const messageToSend = prompt;

    setLastPrompt(messageToSend);

    // Create a human message to add to the UI (using the stored message content)
    const newHumanMessage: HumanMessage = {
      id: -1, //this is a temporary id for the human message and will be updated in the update-message stage
      type: MessageType.USER_MESSAGE,
      sessionId: "",
      threadId: currentThread?.id || "",
      userId: storeUser.id,
      employeeId: currentThread?.employeeId || null,
      content: messageToSend,
      message: {
        content: messageToSend, // Use markup version to preserve mentions
        additional_kwargs: {},
        response_metadata: {},
        kwargs: {
          content: messageToSend,
        },
      },
      pinned: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      thread: currentThread || {
        collectionId: collectionId!,
        id: "",
        sessionId: "",
        threadType: MessageThreadType.NONE,
        personaId: "",
        employeeId: "",
      },
      classification: ClassificationRoute.GENERAL,
    };

    dispatch(addMessage(newHumanMessage));

    // Clear input immediately and allow typing of next message
    dispatch(setPrompt(""));
    setInputValue("");
    setPlainTextPrompt("");

    // Set loading state
    setIsLoading(true);
    setCurrentStatusMessage(null); // Clear any previous status message
    setCurrentSavedMessage(null);
    setCurrentUIObject(null);
    setCurrentAsset(null);

    // Reset user scroll state since they're sending a message and expect to see the response
    userHasScrolledUpRef.current = false;

    // Immediate visual feedback - scroll immediately then smooth follow-up
    scrollToBottomImmediate();
    setTimeout(() => {
      scrollToBottom();
    }, 50);

    sendJsonMessage({
      userId: storeUser.id,
      action: "ask",
      inputMessage: messageToSend, // Use stored message, not current input state
      collectionId: collectionId,
      employeeId: currentThread?.employeeId,
      personaId: currentThread?.personaId,
      threadId: currentThread?.id,
      workspaceId: storeUser.currentWorkspaceId,
      isCanvasModeOpen: isCanvasModeOpen,
      canvasDocumentContent: isCanvasModeOpen
        ? canvasMessage?.message.content || ""
        : undefined,
      messageId: isCanvasModeOpen ? canvasMessage?.id || "" : undefined,
    });
  };

  // Restore prompt after refresh if we previously saved it
  useEffect(() => {
    const saved = sessionStorage.getItem("agentChatSavedPrompt");
    if (saved) {
      dispatch(setPrompt(saved));
      sessionStorage.removeItem("agentChatSavedPrompt");
    }
  }, [dispatch, setPrompt]);

  const handleExpand = (message: AgentResponseMessage) => {
    // Logic for expanding a message
    console.log("Message expanded:", message.id);
  };

  const handleEditInDocument = useCallback(
    (message: AgentResponseMessage) => {
      if (message.type === MessageType.AI_MESSAGE) {
        // Convert regular AI message to canvas message
        openCanvas(message);
      } else if (message.type === MessageType.AI_CANVAS_MESSAGE) {
        // For canvas messages, open canvas mode directly with existing content
        const canvasMessage = message as AIResponseCanvasMessage;
        const htmlContent = marked(
          canvasMessage.message.content || ""
        ) as string;

        const existingLinkRecordId =
          (message as any)?.linkRecordId ??
          (message as any)?.sources?.[0] ??
          (canvasMessage as any)?.linkRecordId ??
          (canvasMessage as any)?.sources?.[0] ??
          null;

        dispatch(
          openCanvasMode({
            content: htmlContent,
            messageId: canvasMessage.id as number,
            title: canvasMessage.message.title || "Untitled Document",
            linkRecordId: existingLinkRecordId,
          })
        );
      }
    },
    [openCanvas, dispatch]
  );

  const handleStartNewThread = async (
    personaId: string | null,
    employeeId: string | null
  ) => {
    if (!storeUser || !collectionId) {
      if (!storeUser) toast("No user available");
      if (!collectionId) toast("No collection selected");
      return;
    }

    // Guard against out-of-sync client state before creating a new thread
    try {
      const lastMessage = messages[messages.length - 1];
      const lastMessageId = lastMessage ? Number(lastMessage.id) : 0;
      const sessionId = currentThread?.sessionId || "";

      const outOfSync = await apiAreMessagesOutOfSync(lastMessageId, sessionId);

      if (outOfSync) {
        sessionStorage.setItem("agentChatSavedPrompt", prompt);
        openOutOfSyncModal();
        return; // Abort thread creation
      }
    } catch (err) {
      console.error("Error checking message sync", err);
      // If the check fails, continue with normal behaviour
    }

    const newThreadMessage = await apiStartNewThread(
      storeUser.id,
      collectionId,
      personaId,
      employeeId
    );

    console.log("newThreadMessage", newThreadMessage);

    if (!newThreadMessage) {
      toast("Failed to start new thread");
      return;
    }

    dispatch(addMessage(newThreadMessage));
    //scroll to bottom
    scrollToBottom();
    // no-op
  };

  const isFetchingExistingMessages =
    isLoadingMore || isLoadingMoreUp || isLoadingMoreDown;

  const shouldShowZeroState =
    !isInitialLoading &&
    !isLoading &&
    !isFetchingExistingMessages &&
    messages.length === 0;

  console.log("[hasNativeHRIS]", hasNativeHRIS);

  return (
    <div className="agent-chat">
      {config.showDebugChat && (
        <Button onClick={() => setShowDebugModal(true)}>Debug Chat</Button>
        // <button
        //   className="agent-chat-debug-button"
        //   onClick={() => setShowDebugModal(true)}>
        //   Debug Chat
        // </button>
      )}

      <div className="agent-chat-content">
        <div className="agent-chat-messages">
          <div
            ref={historyRef}
            className="agent-chat-history-items"
            onScroll={handleScroll}
            style={{ maxHeight: "100%", overflowY: "auto" }}>
            {/* Show LoadingPage for initial loading */}
            {isInitialLoading ? (
              <div className="agent-chat-zero-state">
                <div className="agent-chat-zero-state-content">
                  {/* <LoadingPage
                    loadingMessage="Loading..."
                    className="agent-chat-zero-state-loading-page"
                    disableAnimation={false}
                  /> */}
                </div>
              </div>
            ) : (
              <>
                {messages.length === 0 ? (
                  shouldShowZeroState ? (
                    <div className="agent-chat-zero-state">
                      <div className="agent-chat-zero-state-content">
                        <LoadingPage
                          loadingMessage=""
                          className="agent-chat-zero-state-loading-page"
                          disableAnimation={true}
                        />
                        <div className="agent-chat-zero-state-text">
                          <div className="agent-chat-zero-state-title">
                            No messages yet
                          </div>
                          <div className="agent-chat-zero-state-description">
                            Start a conversation by typing a message below.
                            {notProd && (
                              <>
                                <br />
                                Collection: {collectionId}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null
                ) : (
                  <>
                    <div ref={messagesStartRef} />
                    {isLoadingMoreUp && (
                      <div className="agent-chat-loading-more">
                        <div className="loading-dots">
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                        </div>
                        <span>Loading older messages...</span>
                      </div>
                    )}
                    {messages.map((message: any, index: number) => {
                      // Skip rendering if this is the first message and it's a NEW_THREAD message
                      // if (index === 0 && message.type === MessageType.NEW_THREAD) {
                      //   return null;
                      // }

                      return (
                        <div
                          key={message.id.toString()}
                          data-message-id={message.id}>
                          {message.type === MessageType.USER_MESSAGE && (
                            <HumanMessageComponent
                              message={message}
                              handleStartNewThread={handleStartNewThread}
                              currentThreadId={currentThread?.id || null}
                            />
                          )}

                          {message.type ===
                            MessageType.USER_MESSAGE_CANVAS_FOLLOW_UP && (
                            <HumanMessageCanvasFollowUp
                              message={message}
                              handleStartNewThread={handleStartNewThread}
                            />
                          )}
                          {message.type ===
                            MessageType.USER_MESSAGE_REPORTING_REQUEST && (
                            <HumanMessageReportingRequest
                              message={message}
                              handleStartNewThread={handleStartNewThread}
                            />
                          )}

                          {message.type === MessageType.AI_MESSAGE && (
                            <AIMessage
                              message={message}
                              onExpand={handleExpand}
                              onEditInDocument={handleEditInDocument}
                            />
                          )}
                          {message.type === MessageType.AI_CANVAS_MESSAGE && (
                            <AICanvasMessage
                              message={message}
                              onExpand={handleExpand}
                              onEditInDocument={handleEditInDocument}
                            />
                          )}
                          {message.type === MessageType.AI_MESSAGE_DOCUMENT && (
                            <>
                              <DCComponentRouter
                                key={"dc-component-" + message.id.toString()}
                                message={message}
                                lastUIMessageId={newestUIMessageId}
                                objectName={"asset"}
                                data={JSON.parse(message.content) || {}}
                              />
                            </>
                          )}
                          {message.type === MessageType.AI_MESSAGE_UI && (
                            <>
                              <DCComponentRouter
                                key={"dc-component-" + message.id.toString()}
                                message={message}
                                lastUIMessageId={newestUIMessageId}
                                objectName={JSON.parse(message.content)?.object}
                                data={JSON.parse(message.content) || {}}
                              />
                            </>
                          )}
                          {message.type ===
                            MessageType.AI_MESSAGE_REPORTING && (
                            <DCReporting
                              key={"dc-component-" + message.id.toString()}
                              message={message}
                              data={message.message.reportData}
                              isPreview={false}
                              onCancel={() => window.location.reload()}
                            />
                          )}
                          {message.type === MessageType.TOOL_CALL && (
                            <ToolMessage
                              toolType={
                                message.message.kwargs.tool_calls[0].name
                              }
                            />
                          )}
                          {message.type === MessageType.NEW_THREAD && (
                            <>
                              <NewThreadMessage message={message} />
                            </>
                          )}
                          {message.type === MessageType.AI_STATUS_MESSAGE && (
                            <StatusMessage
                              message={message.message.content}
                              personaIds={message.personaIds}
                              success={true}
                              showCursor={false}
                            />
                          )}
                        </div>
                      );
                    })}
                    {/* Render current status message */}
                    {currentStatusMessage && (
                      <StatusMessage
                        key={currentStatusMessage.id}
                        message={currentStatusMessage.message}
                        personaIds={currentStatusMessage.personaIds}
                        success={currentStatusMessage.success}
                        showCursor={true}
                      />
                    )}
                    {currentUIObject && (
                      <DCComponentRouter
                        key={
                          "dc-component-" + currentSavedMessage?.id.toString()
                        }
                        message={currentSavedMessage}
                        lastUIMessageId={newestUIMessageId}
                        objectName={currentUIObject}
                        data={JSON.parse(currentSavedMessage?.content || "{}")}
                      />
                    )}
                    {currentAsset && (
                      <DCComponentRouter
                        key={
                          "dc-component-" + currentSavedMessage?.id.toString()
                        }
                        message={currentSavedMessage}
                        lastUIMessageId={newestUIMessageId}
                        objectName={"asset"}
                        data={currentMessageData}
                      />
                    )}
                    {isLoading ? (
                      <div
                        className="agent-chat-history-item"
                        style={{ minHeight: "24px" }}>
                        <div className="loading-dots">
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="agent-chat-history-item"
                        style={{ minHeight: "24px" }}></div>
                    )}
                    {currentResponse && (
                      <AIMessageStreaming
                        sources={currentResponse.sources}
                        personaIds={currentResponse.personaIds}
                        content={currentResponse.content}
                      />
                    )}
                    {isLoadingMoreDown && (
                      <div className="agent-chat-loading-more">
                        <div className="loading-dots">
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                          <div className="loading-dot" />
                        </div>
                        <span>Loading newer messages...</span>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="agent-chat-input">
          <div className="agent-chat-input-row">
            {hasNativeHRIS ? (
              <MentionsInputAny
                className="mentions"
                value={inputValue}
                allowSuggestionsAboveCursor={true}
                forceSuggestionsAboveCursor={true}
                suggestionsPortalHost={
                  typeof document !== "undefined" ? document.body : undefined
                }
                onChange={(
                  e: any,
                  newValue: string,
                  newPlainTextValue: string
                ) => {
                  isLocalChangeRef.current = true;
                  setInputValue(newValue);
                  dispatch(setPrompt(newValue)); // Store markup version for backend
                  setPlainTextPrompt(newPlainTextValue); // Store plain text for validation
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" && !e.shiftKey && !isProcessing) {
                    const suggestionsOpen = !!document.querySelector(
                      ".mentions__suggestions__list"
                    );
                    if (!suggestionsOpen && prompt.trim()) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }
                }}
                placeholder="Type your message..."
                disabled={isInitialLoading}
                allowSpaceInQuery={false}
                inputRef={(el: any) => {
                  // Underlying input is a textarea
                  textareaRef.current = el as HTMLTextAreaElement | null;
                }}>
                <MentionAny
                  trigger="@"
                  markup="@[__display__](__id__)"
                  appendSpaceOnAdd={true}
                  displayTransform={(id: string, display: string) =>
                    `@${display}`
                  }
                  style={{
                    control: {
                      fontSize: "inherit",
                      fontWeight: "normal",
                    },
                    highlighter: {
                      overflow: "hidden",
                    },
                    input: {
                      margin: 0,
                      outline: 0,
                      border: 0,
                    },
                    "&multiLine": {
                      control: {
                        fontFamily: "inherit",
                        minHeight: 24,
                      },
                      highlighter: {
                        padding: 8,
                        border: "none",
                      },
                      input: {
                        padding: 8,
                        border: "none",
                        outline: 0,
                      },
                    },
                    mention: {
                      backgroundColor: "#2563eb",
                      color: "#ffffff",
                      border: "1px solid #1d4ed8",
                      borderRadius: "16px",
                      padding: "4px 8px",
                      fontWeight: 600,
                      fontSize: "13px",
                      display: "inline-flex",
                      alignItems: "center",
                      margin: "0 2px",
                      lineHeight: "1.2",
                      verticalAlign: "middle",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                      transition: "all 0.2s ease",
                      textDecoration: "none",
                    },
                    suggestions: {
                      list: {
                        backgroundColor: "white",
                        border: "1px solid #e0e0e0",
                        fontSize: 14,
                        borderRadius: 8,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        maxHeight: 200,
                        overflowY: "auto",
                        zIndex: 1001,
                        position: "absolute",
                        bottom: "100%",
                        top: "auto",
                        left: 0,
                        right: 0,
                        marginBottom: 8,
                        marginTop: 0,
                        width: "100%",
                        minWidth: 250,
                      },
                      item: {
                        display: "flex",
                        alignItems: "center",
                        padding: "8px",
                        cursor: "pointer",
                        color: "var(--text-color-primary, #333)",
                        gap: "8px",
                        borderBottom: "none",
                        "&focused": {
                          backgroundColor: "#f0f0f0",
                        },
                      },
                    },
                  }}
                  data={(
                    query: string,
                    callback: (
                      results: Array<{
                        id: string;
                        display: string;
                        type?: string;
                      }>
                    ) => void
                  ) => {
                    const mentionItems = getMentionData(query);
                    const results = mentionItems.map((item: any) => ({
                      id: item.id,
                      display: item.display,
                      type: item.type, // Include type for custom styling
                    }));
                    callback(results);
                  }}
                  renderSuggestion={(
                    suggestion: any,
                    search: string,
                    highlightedDisplay: React.ReactNode,
                    index: number,
                    focused: boolean
                  ) => {
                    const mentionItem = getMentionData(search).find(
                      (item: any) => item.id === suggestion.id
                    );
                    if (!mentionItem) return null;

                    return renderMentionSuggestion(
                      mentionItem,
                      search,
                      highlightedDisplay,
                      index,
                      focused
                    );
                  }}
                />
              </MentionsInputAny>
            ) : (
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  const newValue = e.target.value;
                  isLocalChangeRef.current = true;
                  setInputValue(newValue);
                  dispatch(setPrompt(newValue));
                  setPlainTextPrompt(newValue); // For plain textarea, markup and plain text are the same
                }}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === "Enter" && !e.shiftKey && !isProcessing) {
                    if (prompt.trim()) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }
                }}
                placeholder="Type your message..."
                disabled={isInitialLoading}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  padding: "8px",
                  fontSize: "1rem",
                  background: "transparent",
                  resize: "none",
                  minHeight: "24px",
                  maxHeight: "120px",
                  fontFamily: "Inter, sans-serif",
                  overflowY: "auto",
                  boxShadow: "none",
                  width: "100%",
                  minWidth: 0,
                }}
              />
            )}

            <div className="agent-chat-icons">
              {config.showPersonas && (
                <CirclePersonaEmployeeSelect
                  disabled={!mergeConnected || isInitialLoading}
                  employees={employees}
                  personas={personas}
                  onChange={(value, isPersona) => {
                    // Check if the selected value is the same as current selection
                    const currentPersonaId = currentThread?.personaId;
                    const currentEmployeeId = currentThread?.employeeId;

                    if (value === "" || value === null) {
                      // Only create new thread if we're not already in a "no filter" state
                      if (currentPersonaId || currentEmployeeId) {
                        handleStartNewThread(null, null);
                      }
                      return;
                    }

                    if (isPersona) {
                      // Only start new thread if different persona is selected
                      if (value !== currentPersonaId) {
                        handleStartNewThread(value, null);
                      }
                    } else {
                      // Only start new thread if different employee is selected
                      if (value !== currentEmployeeId) {
                        handleStartNewThread(null, value);
                      }
                    }
                  }}
                  selectedEmployee={currentThread?.employeeId}
                  selectedPersona={currentThread?.personaId}
                  includeNoFilter={true}
                />
              )}

              {/* <button
                type="button"
                onClick={handleSendMessage}
                disabled={isProcessing || isInitialLoading || !prompt.trim()}>
                {isProcessing ? "Processing..." : "Send"}
              </button> */}

              <div
                className={
                  isProcessing || isInitialLoading || !prompt.trim()
                    ? "agent-chat-send-icon-disabled"
                    : "agent-chat-send-icon"
                }
                onClick={handleSendMessage}
                // disabled={isProcessing || isInitialLoading || !prompt.trim()}
              >
                {isProcessing ? (
                  <Loader size="24" color="grey" />
                ) : (
                  <ArrowUp size="24" color="white" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* NR remove these 10/14 */}
      {/* <div style={{ position: "absolute", top: 100, left: 0, right: 0, bottom: 0, display: showEmployeeRecordCard ? "block" : "none" }}>
        <DCEmployeeRecordProfileCard
          data={{ employeeRecordId: employeeRecordIdMention }}
          isOutdated={false}
        />
      </div> */}

      <ModalDebugChat
        isOpen={showDebugModal}
        onClose={() => setShowDebugModal(false)}
      />

      {/* Out-of-sync confirmation modal */}
      <ModalBasicConfirm
        isOpen={showOutOfSyncModal}
        onClose={closeOutOfSyncModal}
        title="Messages Updated"
        message="New messages have arrived in this conversation. You need to refresh."
        onContinue={handleRefresh}
        continueLabel="Refresh"
        allowDismiss={false}
        allowClickout={false}
      />

      {/* <Toaster /> */}

      {/* Canvas Mode */}
      <CanvasMode />

      {/* Employee Profile Sidebar */}
      <EmployeeProfileSidebar />

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        isOpen={isDocumentPreviewModalOpen}
        onClose={() => dispatch(closeDocumentPreviewModal())}
        document={previewDocument}
      />
    </div>
  );
};
